import React, { useRef, useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

export default function RentalSection() {
  const sectionRef = useRef<HTMLElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting) setIsVisible(true);
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section
      ref={sectionRef}
      className="relative py-20 sm:py-28 md:py-36 bg-white overflow-hidden"
      aria-labelledby="rental-heading"
    >
      <div className="relative max-w-7xl mx-auto px-5 sm:px-8 lg:px-12">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Left: Image */}
          <div
            className={`relative transition-all duration-1000 ${
              isVisible ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-12"
            }`}
          >
            <div className="relative aspect-[4/5] rounded-2xl overflow-hidden bg-neutral-100">
              <img
                src="https://public.readdy.ai/ai/img_res/54786a8c601f572eb2f03b1a62f74ab2.jpg"
                alt="MeVER レンタルサービス - コワーキングスペース"
                className="w-full h-full object-cover object-center"
                loading="lazy"
                width="800"
                height="1000"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-transparent" />
            </div>

            {/* Floating badge */}
            <div
              className={`absolute -bottom-6 -right-6 bg-white rounded-2xl shadow-2xl p-6 transition-all duration-1000 delay-300 ${
                isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
              }`}
            >
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 flex items-center justify-center rounded-full bg-neutral-900">
                  <i className="ri-community-line text-xl text-white" aria-hidden="true" />
                </div>
                <div>
                  <p className="text-xs font-medium text-neutral-400 tracking-wider uppercase">
                    Coworking
                  </p>
                  <p className="text-lg font-light text-neutral-900 tracking-wide">
                    Space
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Right: Content */}
          <div className="relative">
            <div className="text-center mb-10 sm:mb-12 md:mb-16">
              <h2 id="rental-heading" className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold text-black mb-6 sm:mb-8 tracking-tight">
                Try Before<br className="sm:hidden" /> You Commit.
              </h2>
              <p className="text-lg sm:text-xl md:text-2xl font-light text-black/60 leading-relaxed max-w-3xl mx-auto">
                購入前に、MeVERを体験する2つの方法
              </p>
            </div>

            <div className="grid sm:grid-cols-2 gap-6 sm:gap-8 max-w-5xl mx-auto" role="list">
              {/* レンタルサービス */}
              <article className="group relative bg-gradient-to-br from-gray-50 to-white border border-gray-100 rounded-xl sm:rounded-2xl overflow-hidden hover:shadow-xl transition-all duration-300" role="listitem">
                <div className="absolute top-4 sm:top-6 right-4 sm:right-6 z-10">
                  <span className="px-2 sm:px-3 py-1 bg-[#8E7AB5]/10 text-[#8E7AB5] text-[10px] sm:text-xs font-medium rounded-full">
                    2026年夏 開始予定
                  </span>
                </div>
                
                {/* 画像エリア */}
                <div className="relative w-full h-48 sm:h-56 overflow-hidden">
                  <img
                    src="https://readdy.ai/api/search-image?query=modern%20minimalist%20home%20office%20setup%20with%20premium%20ergonomic%20chair%20MeVER%20style%20wooden%20desk%20laptop%20green%20plants%20natural%20lighting%20clean%20aesthetic%20Japanese%20interior%20design%20professional%20photography%20warm%20tones%20simple%20background&width=600&height=400&seq=rental-service-001&orientation=landscape"
                    alt="レンタルサービス - 自宅やオフィスでMeVER製品を体験"
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                    loading="lazy"
                    width="600"
                    height="400"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent"></div>
                </div>

                <div className="p-6 sm:p-8 md:p-10">
                  <div className="w-12 h-12 sm:w-16 sm:h-16 flex items-center justify-center bg-[#8E7AB5]/10 rounded-xl sm:rounded-2xl mb-4 sm:mb-6">
                    <i className="ri-home-office-line text-2xl sm:text-3xl text-[#8E7AB5]" aria-hidden="true"></i>
                  </div>
                  <h3 className="text-xl sm:text-2xl font-bold text-black mb-3 sm:mb-4">
                    レンタルサービス
                  </h3>
                  <p className="text-sm sm:text-base text-black/60 font-light leading-relaxed mb-6 sm:mb-8">
                    ご自宅やオフィスでMeVER製品をじっくりお試しいただけます。法人イベント・短期利用にも対応予定。
                  </p>
                  <Link
                    to="/rental"
                    className="block w-full px-4 sm:px-6 py-3 sm:py-4 bg-[#8E7AB5] text-white text-sm sm:text-base font-light tracking-wide hover:bg-[#7A6A9E] transition-colors whitespace-nowrap rounded-lg cursor-pointer text-center"
                    aria-label="レンタルサービスの詳細ページへ"
                  >
                    詳しく見る
                  </Link>
                </div>
              </article>

              {/* コワーキングスペース体験 */}
              <article className="group relative bg-gradient-to-br from-gray-50 to-white border border-gray-100 rounded-xl sm:rounded-2xl overflow-hidden hover:shadow-xl transition-all duration-300" role="listitem">
                <div className="absolute top-4 sm:top-6 right-4 sm:right-6 z-10">
                  <span className="px-2 sm:px-3 py-1 bg-emerald-500/10 text-emerald-600 text-[10px] sm:text-xs font-medium rounded-full">
                    体験可能
                  </span>
                </div>
                
                {/* 画像エリア */}
                <div className="relative w-full h-48 sm:h-56 overflow-hidden">
                  <img
                    src="https://readdy.ai/api/search-image?query=modern%20coworking%20space%20interior%20with%20people%20working%20on%20laptops%20wooden%20desks%20ergonomic%20chairs%20green%20plants%20natural%20light%20through%20large%20windows%20minimalist%20Japanese%20design%20aesthetic%20warm%20atmosphere%20professional%20photography%20simple%20background&width=600&height=400&seq=coworking-service-001&orientation=landscape"
                    alt="コワーキングスペース体験 - favy新宿でMeVER Chairを体験"
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                    loading="lazy"
                    width="600"
                    height="400"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent"></div>
                </div>

                <div className="p-6 sm:p-8 md:p-10">
                  <div className="w-12 h-12 sm:w-16 sm:h-16 flex items-center justify-center bg-emerald-500/10 rounded-xl sm:rounded-2xl mb-4 sm:mb-6">
                    <i className="ri-building-2-line text-2xl sm:text-3xl text-emerald-600" aria-hidden="true"></i>
                  </div>
                  <h3 className="text-xl sm:text-2xl font-bold text-black mb-3 sm:mb-4">
                    コワーキングスペース体験
                  </h3>
                  <p className="text-sm sm:text-base text-black/60 font-light leading-relaxed mb-3 sm:mb-4">
                    <span className="font-medium text-black">コワーキングスペース by favy（新宿）</span>でMeVER Chairを実際にご体験いただけます。新宿駅徒歩5分の好立地。
                  </p>
                  <p className="text-xs sm:text-sm text-black/50 mb-6 sm:mb-8">
                    ドロップイン 550円/30分〜 ｜ 9:00〜23:00
                  </p>
                  <div className="flex flex-col gap-2.5">
                    <a
                      href="https://appointment.mever.design/"
                      target="_blank"
                      rel="noopener noreferrer nofollow"
                      className="block w-full px-4 sm:px-6 py-3 sm:py-4 bg-black text-white text-sm sm:text-base font-light tracking-wide hover:bg-black/80 transition-colors whitespace-nowrap rounded-lg cursor-pointer text-center"
                    >
                      <i className="ri-calendar-check-line mr-1.5" aria-hidden="true"></i>
                      MeVER Chair 体験予約
                    </a>
                    <Link
                      to="/coworking"
                      className="block w-full px-4 sm:px-6 py-3 sm:py-4 bg-black text-white text-sm sm:text-base font-light tracking-wide hover:bg-black/80 transition-colors whitespace-nowrap rounded-lg cursor-pointer text-center"
                      aria-label="コワーキングスペース体験の詳細ページへ"
                    >
                      詳しく見る
                    </Link>
                  </div>
                </div>
              </article>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
